import { useMemo, useState } from "react";
import { booksApi } from "./api";
import { Capital } from "./Capital";
import { PartnerSummary } from "./PartnerSummary";
import { Plan } from "./Plan";
import { Position } from "./Position";
import { Profit } from "./Profit";
import { Spending } from "./Spending";

/**
 * The books, on the till's own tablet (T014, T021).
 *
 * Two sections for now — where the money is, and what was spent. Capital,
 * Profit and Plan join them as their phases land.
 *
 * Styled like the menu and stock managers, and for the same reason: it is used
 * on the same tablet, often between customers. Lists page rather than scroll
 * inside the dialog.
 */

type Tab = "position" | "spending" | "capital" | "profit" | "plan" | "summary";

export interface BooksManagerProps {
  token: string;
  onClose(): void;
}

export function BooksManager({ token, onClose }: BooksManagerProps) {
  const api = useMemo(() => booksApi(token), [token]);
  const [tab, setTab] = useState<Tab>("position");
  /** Bumped whenever money moves, so the other tab reloads when it is next shown. */
  const [version, setVersion] = useState(0);
  const changed = () => setVersion((n) => n + 1);

  return (
    <div className="books" role="dialog" aria-label="Books">
      <header className="books__head">
        <h2 className="books__title">Books</h2>
        <button type="button" className="books__action" onClick={onClose}>
          Done
        </button>
      </header>

      <nav className="books__tabs" aria-label="Books sections">
        <button
          type="button"
          className={`books__tab${tab === "position" ? " is-active" : ""}`}
          onClick={() => setTab("position")}
        >
          Money
        </button>
        <button
          type="button"
          className={`books__tab${tab === "spending" ? " is-active" : ""}`}
          onClick={() => setTab("spending")}
        >
          Spending
        </button>
        <button
          type="button"
          className={`books__tab${tab === "capital" ? " is-active" : ""}`}
          onClick={() => setTab("capital")}
        >
          Partners
        </button>
        <button
          type="button"
          className={`books__tab${tab === "profit" ? " is-active" : ""}`}
          onClick={() => setTab("profit")}
        >
          Profit
        </button>
        <button
          type="button"
          className={`books__tab${tab === "plan" ? " is-active" : ""}`}
          onClick={() => setTab("plan")}
        >
          Plan
        </button>
        <button
          type="button"
          className={`books__tab${tab === "summary" ? " is-active" : ""}`}
          onClick={() => setTab("summary")}
        >
          Summary
        </button>
      </nav>

      {tab === "position" && <Position api={api} version={version} onChanged={changed} />}
      {tab === "spending" && <Spending api={api} token={token} version={version} onChanged={changed} />}
      {tab === "capital" && <Capital api={api} version={version} onChanged={changed} />}
      {tab === "profit" && <Profit api={api} version={version} />}
      {tab === "plan" && <Plan api={api} version={version} onChanged={changed} />}
      {tab === "summary" && <PartnerSummary api={api} version={version} />}
    </div>
  );
}
